/**
 * IntroSplash — شاشة استهلال بشعار «LECMO / PERFUME» برسم حدود ثمّ تعبئة سوداء.
 *
 * - تُعرض مرّة واحدة لكلّ جلسة تصفّح (sessionStorage) ولا تُعاد عند التنقّل الداخليّ.
 * - قابلة للتفعيل/التعطيل بشكل مستقلّ للهاتف والديسكتوب من /admin/intro-splash
 *   (المفتاح `public_intro_splash` في app_settings) مع تخزين محلّيّ للقيمة لتفادي الوميض.
 * - تمنع التمرير أثناء العرض، وتحترم prefers-reduced-motion، ولها مؤقّت أمان أقصى.
 */
import * as React from "react";
import { usePublicSetting } from "@/hooks/usePublicSetting";
import "./intro-splash.css";

export type IntroSplashConfig = { desktop: boolean; mobile: boolean };

export const INTRO_SPLASH_KEY = "public_intro_splash";
// ⚡ الموبايل معطَّل افتراضيّاً: الشاشة كانت تؤخّر أوّل رسم (FCP/LCP) على الهاتف.
export const DEFAULT_INTRO_SPLASH: IntroSplashConfig = { desktop: true, mobile: false };

const SESSION_FLAG = "lecmo:intro-splash:shown:v1";
const LOCAL_CACHE = "lecmo:intro-splash:config:v1";

/** يجب أن تطابق متغيّرات intro-splash.css بالضبط. */
const DESKTOP_TIMINGS = { draw: 900, fill: 400, exit: 300 } as const;
const MOBILE_TIMINGS = { draw: 600, fill: 300, exit: 300 } as const;
const SAFETY_MS = 3000;

function timings() {
  return isMobileViewport() ? MOBILE_TIMINGS : DESKTOP_TIMINGS;
}

function readCachedConfig(): IntroSplashConfig {
  try {
    const raw = window.localStorage.getItem(LOCAL_CACHE);
    if (!raw) return DEFAULT_INTRO_SPLASH;
    const parsed = JSON.parse(raw) as Partial<IntroSplashConfig>;
    return {
      desktop: parsed.desktop !== false,
      mobile: parsed.mobile !== false,
    };
  } catch {
    return DEFAULT_INTRO_SPLASH;
  }
}

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

export function IntroSplash() {
  const config = usePublicSetting<IntroSplashConfig>(INTRO_SPLASH_KEY, DEFAULT_INTRO_SPLASH);

  // ⚡ LCP: الشاشة تُرسَم من HTML الأوّل (SSR) وحركتها CSS خالصة — لا تنتظر hydration.
  // الإخفاء لمن سبق أن رآها يحدث قبل أيّ رسم عبر class على <html> من سكربت الجذر.
  const [visible, setVisible] = React.useState(true);
  const [exiting, setExiting] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    // إن كان الجذر قد قرّر إخفاءها (جلسة سابقة أو معطَّلة) فلا تشغّل مؤقّتات ولا تقفل التمرير.
    if (document.documentElement.classList.contains("lecmo-no-intro")) {
      setVisible(false);
      return;
    }
    // hydration متأخّر: حركة CSS انتهت فعلاً — أزلها فوراً بلا قفل تمرير.
    const { draw, fill, exit } = timings();
    if (window.performance.now() > draw + fill + exit) {
      setVisible(false);
    }
  }, []);

  // مزامنة النسخة المحلّيّة مع الإعداد الحقيقيّ + إخفاء فوريّ إن كانت الميزة معطَّلة.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LOCAL_CACHE, JSON.stringify(config));
    } catch {
      /* ignore */
    }
    const allowed = isMobileViewport() ? config.mobile : config.desktop;
    if (!allowed) setVisible(false);
  }, [config]);

  // منع التمرير + مؤقّتات الخروج + مؤقّت الأمان
  React.useEffect(() => {
    if (!visible || typeof window === "undefined") return;
    if (document.documentElement.classList.contains("lecmo-no-intro")) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const elapsed = window.performance.now();
    const { draw, fill, exit } = timings();
    const startExitAfter = Math.max(0, (reduce ? 400 : draw + fill) - elapsed);
    const exitDuration = reduce ? 200 : exit;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t1 = window.setTimeout(() => setExiting(true), startExitAfter);
    const t2 = window.setTimeout(() => setVisible(false), startExitAfter + exitDuration);
    const safety = window.setTimeout(() => setVisible(false), Math.max(0, SAFETY_MS - elapsed));

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(safety);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  if (!visible) return null;


  return (
    <div className="intro-splash text-foreground" data-exiting={exiting} aria-hidden="true">
      <svg
        className="intro-splash__svg"
        viewBox="0 0 1000 400"
        role="img"
        aria-label="LECMO PERFUME"
      >
        <defs>
          <mask id="intro-splash-wipe-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="400">
            <rect className="intro-splash__mask-rect" x="0" y="0" width="0" height="400" fill="#fff" />
          </mask>
        </defs>

        {/* الحدود: تُكشَف يساراً ← يميناً بسرعة ثابتة، والسطران متزامنان بنفس القناع */}
        <g className="intro-splash__outline" mask="url(#intro-splash-wipe-mask)">
          <text
            x="500"
            y="210"
            textAnchor="middle"
            fontFamily="var(--font-serif, Georgia, serif)"
            fontSize="200"
            letterSpacing="14"
          >
            LECMO
          </text>
          <text
            x="500"
            y="310"
            textAnchor="middle"
            fontFamily="var(--font-sans, system-ui, sans-serif)"
            fontSize="72"
            letterSpacing="34"
          >
            PERFUME
          </text>
        </g>

        {/* التعبئة السوداء للمجموعة كاملة بعد انتهاء الرسم */}
        <g className="intro-splash__fill">
          <text
            x="500"
            y="210"
            textAnchor="middle"
            fontFamily="var(--font-serif, Georgia, serif)"
            fontSize="200"
            letterSpacing="14"
          >
            LECMO
          </text>
          <text
            x="500"
            y="310"
            textAnchor="middle"
            fontFamily="var(--font-sans, system-ui, sans-serif)"
            fontSize="72"
            letterSpacing="34"
          >
            PERFUME
          </text>
        </g>
      </svg>
    </div>
  );
}
